import { Component, OnInit, Input } from '@angular/core';
import { TradingService } from '../../../../services/common/trading-service.service';
import { TransactionType, OrderType, OrderDuration} from './../../../../models/common/trading-common.model';
import { TradingEntitlements } from './../../../../models/common/trading-entitlements';

@Component({
  selector: 'cancel-equity-order-details',
  templateUrl: './cancel-equity-order-details.component.html',
  styleUrls: ['./cancel-equity-order-details.component.css']
})
export class CancelEquityOrderDetailsComponent implements OnInit {
  @Input() order: any;
  @Input() nonDollarTrade: boolean;
  @Input() tradingEntitlements: TradingEntitlements;
  @Input() isBdUser: boolean;
  @Input() isRiaUser: boolean;
  @Input() isDualUser: boolean;
  @Input() isOmniBus: boolean;

  activeTheme: string;

  orderTitleStyle: string = 'tr-OrderSubTitle_Buy';
  marketOrder: boolean;
  gtdOrder: boolean;
  showLocation: boolean;
  shortSaleExemptOrder: boolean;
  sellShortOrder: boolean;
  accountType: string;
  acctTypeLabel: string = 'labels.accountType';

  showAdditionalInfo: boolean = false;
  showIpSplitCommission: boolean = false;
  showCommission: boolean = false;
  showRegulatoryInfo: boolean = false;
  showExecutionPrice: boolean = false;
  showTrailingBasedOn: boolean = false;
  showSolicited: boolean = false;

  constructor(private tradingService: TradingService) { }

  ngOnInit() {
    this.activeTheme = this.tradingService.getActiveThemeFromCurrentContainer();

    this.setCoreSectionData();
    this.setAdditionalInfoSectionData();
    this.setIPSplitSectionData();
    this.setRegulatoryInfoData();

    if (this.isDualUser) {
      this.showSolicited = false;
    } else if (this.isBdUser) {
      this.showSolicited = true;
    } else if (this.isRiaUser && this.order.account.ibdId === 'C01' && this.order.account.acctProdtProfileCd === 'PMAS') {
      this.showSolicited = true;
    } else if (this.tradingEntitlements.isRetailUser) {
      this.showSolicited =  this.tradingEntitlements.isSolicitedEnabled || this.tradingEntitlements.isNonSolicitedEnabled;
  }
  }

  setCoreSectionForRetailUser() {
    if (this.order.orderType === OrderType.LIMIT && this.order.duration === OrderDuration.FOK) {
      this.order.orderType = OrderType.FOK;
      this.order.duration = OrderDuration.DAY;
    } else if (this.order.orderType === OrderType.LIMIT && this.order.additionalInfoAon === 'All or None') {
      this.order.orderType = OrderType.AON;
    } else if (this.order.orderType === OrderType.TRAILING_STOP_LIMIT_DOLLAR && this.order.additionalInfoAon === 'All or None') {
      this.order.orderType = OrderType.TRAILING_STOP_LIMIT_AON;
    } else if (this.order.orderType === OrderType.TRAILING_STOP_LIMIT_PCT && this.order.additionalInfoAon === 'All or None') {
      this.order.orderType = OrderType.TRAILING_STOP_LIMIT_PCT_AON;
    }
  }
  
  setAccountTypeSectionData() {
    if (this.order.account != null && this.order.account.custodianCd != null && this.order.account.custodianCd === 'BNYM') {
      this.accountType = this.order.balTypeCd === 'I' ? 'Income' : 'Principal';
      this.acctTypeLabel = 'labels.balanceType';
    } else {
      this.accountType = this.order.accountType;
      this.acctTypeLabel = 'labels.accountType';
    }
  }

  setCoreSectionData() {
    // For core section
    let transactionType = this.order.transactionType;
    if (transactionType === TransactionType.BUY || transactionType === TransactionType.BUY_TO_COVER) {
      this.orderTitleStyle = 'tr-OrderSubTitle_Buy';
    } else {
        this.orderTitleStyle = 'tr-OrderSubTitle_Sell';
    }

    // Trailing Based On is coming back with blank spaces even when empty, so call trim on the value when it's defined
    if ( this.tradingEntitlements.isProfessionalUser && !this.nonDollarTrade && !!this.order.trailingBasedOn ) {
      this.order.trailingBasedOn = this.order.trailingBasedOn.trim();
    }

    if (this.tradingEntitlements.isRetailUser) {
      this.setCoreSectionForRetailUser();
    }

    this.marketOrder = (this.order.orderType === OrderType.MARKET);
    this.gtdOrder = (this.order.duration === OrderDuration.GTD);
    this.showLocation = (this.order.docLocation &&
      (transactionType === TransactionType.SELL_NOT_IN_ACCOUNT
          || transactionType === TransactionType.SELL_FROM_PORTFOLIO
          || transactionType === TransactionType.SHORT_SALE_EXEMPT));
    this.shortSaleExemptOrder = (transactionType === TransactionType.SHORT_SALE_EXEMPT);
    this.sellShortOrder = (transactionType === TransactionType.SELL_SHORT);

    this.showExecutionPrice = !!this.order.tradePrice && !isNaN(this.order.tradePrice);

    const isTrailingBasedOn: boolean = this.order.orderType === OrderType.TRAILING_STOP_DOLLAR ||
          this.order.orderType === OrderType.TRAILING_STOP_LIMIT_DOLLAR ||
          this.order.orderType === OrderType.TRAILING_STOP_PCT ||
          this.order.orderType === OrderType.TRAILING_STOP_LIMIT_PCT;

    this.showTrailingBasedOn = this.tradingEntitlements.isProfessionalUser && !this.nonDollarTrade &&
      isTrailingBasedOn;
      
    this.setAccountTypeSectionData();
  }

  setAdditionalInfoForBdUser() {
    this.showAdditionalInfo = ((!!this.order.addlOrdData && this.order.addlOrdData.length > 0) ||
          !!this.order.floorInst ||
          !!(this.order.discExercised || '').trim() ||
          (!!this.order.paInd && !this.nonDollarTrade) ||
          (!!(this.order.addlIbdMemo || '').trim() && !this.nonDollarTrade) ||
          !!(this.order.trailer || '').trim() ||
          (!!this.order.sourceOverride && !this.nonDollarTrade));
  }

  setAdditionalInfoSectionData() {
    // Backend sometimes sends [null] in addlOrdData
    if (Array.isArray(this.order.addlOrdData)) {
      this.order.addlOrdData = this.order.addlOrdData.filter(n => n);    // remove null, undefined, '' from array
    }

    // For additional-info section
    if (this.isBdUser) {
      this.setAdditionalInfoForBdUser();
    } else if (this.tradingEntitlements.isRetailUser) {
      this.showAdditionalInfo = false;
    } else {
      this.showAdditionalInfo = ((!!this.order.addlOrdData && this.order.addlOrdData.length > 0) ||
          !!this.order.floorInst ||
          !!this.order.trailer ||
          !!(this.order.discExercised || '').trim());
    }
  }

  setIPSplitSectionData() {
    if (!!this.order.commission) {
      this.showIpSplitCommission = (!!this.order.commission.splitIP1 || !!this.order.commission.groupId);
      this.showCommission = this.nonDollarTrade && (!!this.order.commission.commType &&
        !!this.order.commission.commissionAmt);
    }

    if (this.tradingEntitlements.isRetailUser) {
      this.showIpSplitCommission = false;
    }
  }

  setRegulatoryInfoData() {
    this.showRegulatoryInfo = !this.isRiaUser && (!!(this.order.secIbdOrdRcvDate || '').trim() ||
      !!(this.order.secTermsAndConditions || '').trim() ||
      !!(this.order.secIbdOrdRcvFromName || '').trim() ||
      !!(this.order.oatsDeskReceiptDate1 || '').trim() ||
      !!(this.order.oatsDeskTypeCode1 || '').trim() ||
      !!(this.order.ordXctnTxt1 || '').trim() ||
      !!(this.order.oatsDeskReceiptDate2 || '').trim() ||
      !!(this.order.oatsDeskTypeCode2 || '').trim() ||
      !!(this.order.ordXctnTxt2 || '').trim());

    if (this.tradingEntitlements.isRetailUser) {
        this.showRegulatoryInfo = false;
      }
  }



  formatBackendDate(sourceDate: string): string {     // yyyymmdd => mm/dd/yyyy
    if (!!sourceDate) {
        return sourceDate.substring(4, 6) + '/' + sourceDate.substring(6, 8) + '/' + sourceDate.substring(0, 4);
    } else {
        return '';
    }
  }

  formatBackendTime(sourceTime: string): string {
    if ( !!sourceTime && sourceTime !== '' ) {
        return sourceTime.substring(0, 2) + ':' + sourceTime.substring(2, 4) + ':' + sourceTime.substring(4);
    } else {
        return sourceTime;
    }
  }
}
